import { useCallback, useState } from "react";
import { getContactCounts } from "../lib/contacts";
import { api, type GraphData, type NodeData, type Structure, type StructureContact, getAuthToken } from "../lib/api";

// Helper to decode JWT payload
const decodeJwtPayload = (token: string) => {
  try {
    const base64 = token.split(".")[1];
    if (!base64) return null;
    const normalized = base64.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), "=");
    return JSON.parse(atob(padded));
  } catch (err) {
    console.warn("[token] Failed to decode payload", err);
    return null;
  }
};

// Helper to check if user has valid edit token for a structure
const hasValidEditToken = (structureId: string): boolean => {
  const token = getAuthToken();
  if (!token) return false;
  try {
    const payload = decodeJwtPayload(token);
    if (!payload) return false;
    const expOk = !payload.exp || payload.exp * 1000 > Date.now();
    if (!expOk) return false;
    if (payload.sub !== structureId) return false;
    return payload.can_edit === true;
  } catch (err) {
    console.warn("[token] Failed to validate edit token", err);
    return false;
  }
};

type Params = {
  structureId?: string;
  shareToken?: string;
  viewOnly?: boolean;
  resolveContacts: (node: NodeData, lookupOverride?: Record<string, StructureContact>) => any[];
  toggleCollapse: (id: string) => void;
  applyGraphState: (nodes: any[], edges: any[], opts?: { throttle?: boolean }) => void;
  resetHistory: (graph: GraphData) => void;
  setViewOnlyMode: (v: boolean) => void;
  loadContactsForStructure: (id: string) => Promise<Record<string, StructureContact>>;
  initialSettingsOpenedRef: React.MutableRefObject<boolean>;
  setShowSettings: (v: boolean) => void;
}; 

export function useGraphData({
  structureId,
  shareToken,
  viewOnly,
  resolveContacts,
  toggleCollapse,
  applyGraphState,
  resetHistory,
  setViewOnlyMode,
  loadContactsForStructure,
  initialSettingsOpenedRef,
  setShowSettings,
}: Params) {
  const [structure, setStructure] = useState<Structure | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const applySnapshot = useCallback(
    (graph: GraphData, forceViewOnly?: boolean, contactsOverride?: Record<string, StructureContact>) => {
      const readonly = forceViewOnly ?? viewOnly;
      const nodes = graph.nodes.map((n) => {
        const position = n.position || { x: n.x ?? 0, y: n.y ?? 0 };
        const contactIds = (n as any).contact_ids || [];
        const contacts = resolveContacts(n as NodeData, contactsOverride);
        const contactsCount = contacts.length || contactIds.length;
        const { board: boardContactsCount, group: groupContactsCount } = getContactCounts(contacts as any);
        const linksCount = (n.links || []).length;
        const notesCount = (n.notes || []).length;
        return {
          id: n.id,
          type: n.shape || "atlas",
          position,
          data: {
            ...n,
            contact_ids: contactIds,
            contacts,
            position,
            highlighted: n.highlighted ?? false,
            contactsCount,
            boardContactsCount,
            groupContactsCount,
            linksCount,
            notesCount,
            onToggleCollapse: () => toggleCollapse(n.id),
            viewOnly: readonly,
          },
          draggable: !readonly,
          selectable: true,
        };
      });
      const edges = graph.edges.map((e) => ({
        id: e.id,
        source: e.source,
        target: e.target,
        sourceHandle: e.source_handle || undefined,
        targetHandle: e.target_handle || undefined,
        type: e.line_style === "step" ? "orthogonal" : e.line_style === "straight" ? "straight" : "default",
        animated: e.arrow_style === "animated",
        data: { ...e, arrow_style: e.arrow_style || "default", routing: e.routing || { mode: "auto", bendOffset: 0 } },
      }));
      applyGraphState(nodes, edges);
    },
    [applyGraphState, resolveContacts, toggleCollapse, viewOnly]
  );

  const loadGraph = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (shareToken) {
        const payload = await api.getShared(shareToken);
        const contactMap = await loadContactsForStructure(payload.structure.id);
        const graph: GraphData = { structure_id: payload.structure.id, nodes: payload.nodes, edges: payload.edges };
        setStructure({
          id: payload.structure.id,
          name: payload.structure.name,
          description: payload.structure.description || "",
          tags: payload.structure.tags || [],
          share_enabled: true,
          share_token: shareToken,
        });
        setViewOnlyMode(true);
        resetHistory(graph);
        applySnapshot(graph, true, contactMap);
      } else if (structureId) {
        const [detail, graph, contactMap] = await Promise.all([
          api.getStructure(structureId),
          api.getGraph(structureId),
          loadContactsForStructure(structureId),
        ]);
        setStructure(detail);

        // Determine if view-only mode should be enabled
        // Only enable view-only if:
        // 1. Explicitly requested via viewOnly prop, OR
        // 2. Password is enabled AND user doesn't have valid edit token
        const hasEditAccess = hasValidEditToken(structureId);
        const readonly = viewOnly || (!!detail.edit_password_enabled && !hasEditAccess);

        setViewOnlyMode(readonly);
        resetHistory(graph);
        applySnapshot(graph, readonly, contactMap);

        const shouldAutoOpen = !readonly && !initialSettingsOpenedRef.current && (graph.nodes.length === 0);
        if (shouldAutoOpen) {
          initialSettingsOpenedRef.current = true;
          setShowSettings(true);
        }
      }
      return true;
    } catch (err: any) {
      setError(err.message);
      return false;
    } finally {
      setLoading(false);
    }
  }, [applySnapshot, initialSettingsOpenedRef, loadContactsForStructure, resetHistory, setShowSettings, setViewOnlyMode, shareToken, structureId, viewOnly]);

  const applySnapshotRef = useCallback((graph: GraphData, readonly?: boolean, contacts?: Record<string, StructureContact>) => {
    applySnapshot(graph, readonly, contacts);
  }, [applySnapshot]);

  return { structure, setStructure, loading, error, applySnapshot: applySnapshotRef, loadGraph, setError };
}
